import { API_METHODS } from '@compass/client/constants/api-methods';
import type { CallInfo } from '@compass/client/types/call';
import { CallStatus, CallType } from '@compass/client/types/call';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { useStore } from '@store/index';
import { formatCallType, getCallDuration, getCallStatusDisplay } from '@utils/calls';
import {
  Check,
  CircleStop,
  Copy,
  ExternalLink,
  MousePointerClick,
  TerminalSquare,
  X,
} from 'lucide-react';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { EditableChartNotes } from '@/components/EditableChartNotes';
import { FormattedDateTime } from '@/components/FormattedDateTime';
import { WorkflowType } from '@/constants/dispositions';
import { useCallStack } from '@/hooks/useCallStack';
import { useCanCallAPI } from '@/hooks/usePermissions';
import { useUsersByIds } from '@/hooks/useUsers';
import { determineAttemptType } from '@/utils/callsTabs/statusDisplay';
import { copyToClipboard } from '@/utils/clipboard';
import { formatPayerHoursToday } from '@/utils/payerHours';
import { buildRoutePath } from '@/utils/routes';
import { isRecallSchedulerWorkflow } from '@/utils/workflow';

import type { PopoverContentProps, TabLabelContext, TabTypeHandler } from './types';

const getLabel = (_data: CallInfo, context: TabLabelContext): string => {
  const { attemptNumber, workflowType, caseState } = context;

  if (
    workflowType === WorkflowType.MEDBV ||
    workflowType === WorkflowType.UNIFIEDMEDBV ||
    workflowType === WorkflowType.BHO ||
    workflowType === WorkflowType.PA_STATUS_PBM ||
    workflowType === WorkflowType.PA_STATUS_MEDBV
  ) {
    return `Attempt ${attemptNumber}`;
  }

  if (workflowType === WorkflowType.MEDICARE_HUMAN) {
    const attemptType = determineAttemptType(caseState);
    return `${attemptType} Attempt ${attemptNumber}`;
  }

  return `Call ${attemptNumber}`;
};

const getStatusLabel = (data: CallInfo): string => {
  return getCallStatusDisplay(data.status as CallStatus);
};

// Two vocabularies share this field. The AI stack (call-scheduler) writes the
// first group; human calls write the second (BV-2130). They are kept distinct
// because "Agent Ended Call" reads as the bot on an AI call and as a person on a
// human one — the same word for two different actors.
const END_REASON_LABELS: Record<string, string> = {
  peer: 'Callee Hung Up',
  worker: 'Agent Ended Call',
  worker_failure: 'Agent Failure',
  operator: 'Operator Cancelled',
  carrier: 'Carrier Failure',
  twilio_drop: 'Connection Dropped',
  queue: 'Queue Timeout',
  scheduler: 'System Cancelled',
  // Human calls.
  rep: 'Rep Hung Up',
  payer: 'Payer Hung Up',
  unknown: 'Ended — source unknown',
};

type SplitState = 'processing' | 'failed' | 'no-recording';

const SPLIT_STATE_LABELS: Record<SplitState, string> = {
  processing: 'Processing recording',
  failed: 'Could not be split',
  'no-recording': 'No recording available',
};

// A stacked call's recording is fetched, transcribed and split into one slice
// per covered case; that runs after the call is marked completed, so there is
// a window where a case's own assets are not there yet. Derived once from the
// call's own flags/assets so exactly one badge renders below, rather than
// scattering these conditions across the JSX.
//
// A FAILED split leaves the whole, unsliced recording in `recording` — the
// `fat_recording` pointer is only ever written on the SUCCESS path — so
// "no recording at all" must check both, preferring `fat_recording`. Getting
// this backwards would hide a perfectly playable recording behind
// "No recording available".
const getSplitState = (call: CallInfo): SplitState | null => {
  const flags = call.status_flags;
  if (flags?.stack_split_in_progress) return 'processing';
  if (!flags?.stack_split_failed_reason) return null;
  const recordingAssetId = call.assets?.[0]?.fat_recording || call.assets?.[0]?.recording;
  return recordingAssetId ? 'failed' : 'no-recording';
};

const formatEndReason = (v?: string): string => {
  if (!v) return '';
  if (END_REASON_LABELS[v]) return END_REASON_LABELS[v];
  // Fallback: title-case any unmapped snake_case value (e.g. "some_reason" -> "Some Reason")
  return v
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const CallPopoverContent: React.FC<PopoverContentProps<CallInfo>> = ({
  data: call,
  workflowType,
  onCancelCall,
  onUpdateChartNotes,
  onSelectCall,
}) => {
  const { selectedOrganizationId, selectedProgramId, getTimeZone } = useStore();
  // Internal debug view of the AI agent's files; only the global Console Viewer role holds it.
  const canOpenConsole = useCanCallAPI(API_METHODS.AssetService.ListCallConsoleAssets);
  const showConsoleLink =
    canOpenConsole && call.type === CallType.CALL_TYPE_AI && !!call.id && !!call.caserecord_id;

  // When the payer can be reached today, in the timezone picked in the header,
  // so a Scheduled AI call can be told apart from a stuck one (BV-2480).
  const payerHoursLabel = formatPayerHoursToday(call.payer_hours, getTimeZone());

  // Resolve the calling user's hex id to a display name. user_id is stamped
  // server-side from the authenticated creator for human calls (BI-1685);
  // older calls may not carry it, in which case the row is omitted.
  const callerId = call.type === CallType.CALL_TYPE_HUMAN ? call.user_id : undefined;
  const callerIds = useMemo(() => (callerId ? [callerId] : []), [callerId]);
  const { usersMap } = useUsersByIds(callerIds, !!callerId);
  const callerName = callerId
    ? (usersMap.get(callerId)?.name ?? usersMap.get(callerId)?.email ?? callerId)
    : null;

  // A stacked call is one conversation that covered several cases. The call the
  // agent dialled points its stack_id at itself; every covered case has its own
  // call carrying that same id.
  const stack = call.call_stack;
  const isStackParent = Boolean(stack?.stack_id && stack.stack_id === call.id);

  // Only the parent needs the sibling list — a covered case already carries its
  // parent's id and code inline, so that direction costs no query. The hook is
  // gated on a defined call id, and this popover is unmounted until it opens,
  // so an ordinary unstacked call fires nothing at all.
  const { data: stackData } = useCallStack(isStackParent ? call.id : undefined);
  const coveredCalls = (stackData?.members ?? []).filter(member => member.call_id !== call.id);

  // Links point at the case page with that case's own call preselected. Each
  // side carries its own program id because a stack may span programs; stacks
  // written before those fields existed carry none, and for those the viewer's
  // program is correct because they were single-program by construction.
  const caseCallLink = (caseId: string, programId?: string, callId?: string) => {
    const path = buildRoutePath(
      `/cases/${caseId}`,
      selectedOrganizationId ?? '',
      programId || (selectedProgramId ?? ''),
    );
    return callId ? `${path}?callId=${callId}` : path;
  };

  // Renders the parent's stacked-calls row: singular with the bare call id when
  // there is exactly one, plural with compact "Call N" links (full id on hover
  // via `title`) when there are several. Zero stacked calls renders nothing.
  const renderStackedCallsRow = () => {
    if (coveredCalls.length === 1) {
      const covered = coveredCalls[0];
      return (
        <div className="flex justify-between gap-8">
          <span className="text-muted-foreground">Stacked call:</span>
          <Link
            to={caseCallLink(
              covered.case_record_id ?? '',
              covered.case_program_id,
              covered.call_id,
            )}
            className="text-primary hover:underline"
          >
            {covered.call_id}
          </Link>
        </div>
      );
    }

    if (coveredCalls.length > 1) {
      return (
        <div className="flex justify-between gap-8">
          <span className="text-muted-foreground">Stacked calls:</span>
          <span className="text-right">
            {coveredCalls.map((covered, index) => (
              <React.Fragment key={covered.call_id ?? index}>
                {index > 0 && <span className="mx-1.5 text-muted-foreground">|</span>}
                <Link
                  to={caseCallLink(
                    covered.case_record_id ?? '',
                    covered.case_program_id,
                    covered.call_id,
                  )}
                  className="inline-flex items-center gap-1 align-middle text-primary hover:underline"
                  title={covered.call_id}
                >
                  {`Call ${index + 1}`}
                  <ExternalLink className="h-3 w-3 shrink-0" />
                </Link>
              </React.Fragment>
            ))}
          </span>
        </div>
      );
    }

    return null;
  };

  const splitState = getSplitState(call);

  const isRecallScheduler = isRecallSchedulerWorkflow(workflowType);
  const canEditReachoutNote =
    !!onUpdateChartNotes &&
    (isRecallScheduler
      ? !call.chart_info?.task_updated
      : !!call.chart_info && !call.chart_info.task_updated);

  const showNotesSection = call.chart_info || isRecallScheduler;

  return (
    <>
      <div className="space-y-2 text-sm">
        <div className="flex justify-between gap-8 items-center">
          <span className="text-muted-foreground">Call ID:</span>
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs">{call.id}</span>
            {call.id && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(call.id!)}
                className="h-6 w-6 p-0"
              >
                <Copy className="w-3 h-3" />
              </Button>
            )}
          </div>
        </div>
        <div className="flex justify-between gap-8">
          <span className="text-muted-foreground">Type:</span>
          <span>{formatCallType(call.type)}</span>
        </div>
        {callerName && (
          <div className="flex justify-between gap-8">
            <span className="text-muted-foreground">Called by:</span>
            <span>{callerName}</span>
          </div>
        )}
        <div className="flex justify-between gap-8">
          <span className="text-muted-foreground">Status:</span>
          <span>{getCallStatusDisplay(call.status as CallStatus)}</span>
        </div>
        {call.agent_id && (
          <div className="flex justify-between gap-8">
            <span className="text-muted-foreground">Agent ID:</span>
            <span className="font-mono text-xs">{call.agent_id}</span>
          </div>
        )}
        {call.disposition && (
          <div className="flex justify-between gap-8">
            <span className="text-muted-foreground">Disposition:</span>
            <Badge variant="secondary">{call.disposition.type || '--'}</Badge>
          </div>
        )}
        <div className="flex justify-between gap-8">
          <span className="text-muted-foreground">Phone:</span>
          <span>{call.phone}</span>
        </div>
        {call.out_phone && (
          <div className="flex justify-between gap-8">
            <span className="text-muted-foreground">Out Phone:</span>
            <span>{call.out_phone}</span>
          </div>
        )}
        {payerHoursLabel && (
          <div className="flex justify-between gap-8">
            <span className="text-muted-foreground">Payer Hours:</span>
            <span>{payerHoursLabel}</span>
          </div>
        )}
        {call.started_at && (
          <div className="flex justify-between gap-8">
            <span className="text-muted-foreground">Started:</span>
            <FormattedDateTime value={call.started_at} />
          </div>
        )}
        {call.ended_at && (
          <div className="flex justify-between gap-8">
            <span className="text-muted-foreground">Duration:</span>
            <span>{getCallDuration(call.started_at, call.ended_at)}</span>
          </div>
        )}
        {call.end_reason?.terminated_by && (
          <div className="flex justify-between gap-8">
            <span className="text-muted-foreground">End Reason:</span>
            <span>{formatEndReason(call.end_reason.terminated_by)}</span>
          </div>
        )}

        {stack?.stack_id &&
          (isStackParent
            ? renderStackedCallsRow()
            : stack.parent_case_id && (
                <div className="flex justify-between gap-8">
                  <span className="text-muted-foreground">Stacked On:</span>
                  <Link
                    to={caseCallLink(
                      stack.parent_case_id,
                      stack.parent_case_program_id,
                      stack.stack_id,
                    )}
                    className="text-primary hover:underline"
                  >
                    {stack.stack_id}
                  </Link>
                </div>
              ))}

        {splitState && (
          <div className="flex justify-between gap-8 items-center">
            <span className="text-muted-foreground">Split:</span>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">{SPLIT_STATE_LABELS[splitState]}</Badge>
              {splitState === 'failed' && (
                <Link
                  to={caseCallLink(call.caserecord_id ?? '', stack?.case_program_id, call.id)}
                  className="text-primary hover:underline"
                >
                  full call
                </Link>
              )}
            </div>
          </div>
        )}
      </div>

      {showConsoleLink && (
        <div className="pt-2 border-t">
          <Button asChild variant="outline" size="sm" className="w-full text-xs">
            <Link
              to={buildRoutePath(
                `/cases/${call.caserecord_id}/calls/${call.id}/console`,
                selectedOrganizationId ?? '',
                selectedProgramId ?? '',
              )}
            >
              <TerminalSquare className="w-3 h-3 mr-1" />
              Open Console
            </Link>
          </Button>
        </div>
      )}

      {onSelectCall && call.id && (
        <div className="pt-2 border-t">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onSelectCall(call.id!)}
            className="w-full text-xs"
          >
            <MousePointerClick className="w-3 h-3 mr-1" />
            Attach To Case
          </Button>
        </div>
      )}

      {call.status === CallStatus.CALL_STATUS_IN_PROGRESS && onCancelCall && (
        <div className="pt-2 border-t">
          <Button
            variant="destructive"
            size="sm"
            onClick={() => onCancelCall(call.id!)}
            className="w-full text-xs"
          >
            <CircleStop className="w-3 h-3 mr-1" />
            End Call
          </Button>
        </div>
      )}

      {call.disposition?.notes && (
        <div className="">
          <div className="bg-secondary p-2 rounded text-xs">
            <div className="text-muted-foreground mb-1">Notes:</div>
            <div className="text-foreground whitespace-pre-wrap max-w-[400px] max-h-20 overflow-auto">
              {call.disposition.notes}
            </div>
          </div>
        </div>
      )}

      {showNotesSection && (
        <>
          {call.chart_info && (
            <div className="flex justify-between gap-8 items-center text-sm">
              <span className="text-muted-foreground">Reach-out Status Updated</span>
              {call.chart_info.chart_updated && call.chart_info.task_updated ? (
                <div className="flex items-center justify-center w-5 h-5 rounded-full bg-green-500">
                  <Check className="w-3 h-3 text-white" />
                </div>
              ) : (
                <div className="flex items-center justify-center w-5 h-5 rounded-full bg-red-500">
                  <X className="w-3 h-3 text-white" />
                </div>
              )}
            </div>
          )}
          {call.id && (
            <EditableChartNotes
              chartInfo={call.chart_info}
              callId={call.id}
              onSave={onUpdateChartNotes}
              canEdit={canEditReachoutNote}
            />
          )}
        </>
      )}
    </>
  );
};

export const callHandler: TabTypeHandler<CallInfo> = {
  isDisabled: false,
  getLabel,
  getStatusLabel,
  PopoverContent: CallPopoverContent,
};
